import React from 'react';
import {
  Box,
  Grid,
  TextField,
  InputAdornment,
  Button,
  Typography,
  LinearProgress,
  Stack,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Lock as LockIcon,
  Visibility,
  VisibilityOff,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material';
import { useTranslation } from '../../../hooks/useTranslation';

const ClientSignUpForm = ({
  formData,
  handleInputChange,
  showPassword,
  setShowPassword,
  showConfirmPassword,
  setShowConfirmPassword,
  passwordStrength
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();

  const textFieldStyles = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 2,
      backgroundColor: 'rgba(255,255,255,0.05)',
      color: 'white',
      '& fieldset': {
        borderColor: 'rgba(255,255,255,0.2)',
      },
      '&:hover fieldset': {
        borderColor: '#f5f5dc',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#f5f5dc',
      },
    },
    '& .MuiInputLabel-root': {
      color: 'rgba(255,255,255,0.7)',
      '&.Mui-focused': {
        color: '#f5f5dc',
      },
    },
  };

  if (isMobile) {
    return (
      <Box sx={{ mb: { xs: 3, md: 4 } }}>
        <Typography 
          variant="h6" 
          sx={{ 
            fontWeight: 600, 
            mb: 3, 
            color: 'white',
            fontSize: '1.1rem',
            textAlign: 'center'
          }}
        >
          {t('auth.yourInformation')}
        </Typography>

        <Stack spacing={2.5}>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label={t('auth.firstName')}
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                required
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ color: '#f5f5dc', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={textFieldStyles}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label={t('auth.lastName')}
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                required
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonIcon sx={{ color: '#f5f5dc', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                }}
                sx={textFieldStyles}
              />
            </Grid>
          </Grid>
          
          <TextField
            fullWidth
            label={t('auth.email')}
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            required
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon sx={{ color: '#f5f5dc', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={textFieldStyles}
          />
          
          <TextField
            fullWidth
            label={t('auth.phone')}
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            placeholder={t('auth.phonePlaceholder')}
            required
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon sx={{ color: '#f5f5dc', fontSize: 20 }} />
                </InputAdornment>
              ),
            }}
            sx={textFieldStyles}
          />
          
          <TextField
            fullWidth
            label={t('auth.password')}
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleInputChange}
            required
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ color: '#f5f5dc', fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    onClick={() => setShowPassword(!showPassword)}
                    sx={{ 
                      minWidth: 'auto', 
                      p: 1,
                      color: 'rgba(255,255,255,0.7)',
                      '&:hover': {
                        color: '#f5f5dc'
                      }
                    }}
                  >
                    {showPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                  </Button>
                </InputAdornment>
              ),
            }}
            sx={textFieldStyles}
          />
          
          {formData.password && (
            <Box sx={{ mt: 1 }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.75rem' }}>
                  {t('auth.passwordStrength')}:
                </Typography>
                <Typography
                  variant="caption"
                  color={passwordStrength < 50 ? 'error.main' : passwordStrength < 75 ? 'warning.main' : 'success.main'}
                  sx={{ fontWeight: 600, fontSize: '0.75rem' }}
                >
                  {passwordStrength < 50 ? t('auth.weak') : passwordStrength < 75 ? t('auth.medium') : t('auth.strong')}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={passwordStrength}
                sx={{
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: passwordStrength < 50 ? '#ef4444' : passwordStrength < 75 ? '#f59e0b' : '#10b981',
                    borderRadius: 2,
                  },
                }}
              />
            </Box>
          )}
          
          <TextField
            fullWidth
            label={t('auth.confirmPassword')}
            name="confirmPassword"
            type={showConfirmPassword ? 'text' : 'password'}
            value={formData.confirmPassword}
            onChange={handleInputChange}
            required
            variant="outlined"
            size="small"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ color: '#f5f5dc', fontSize: 20 }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    sx={{ 
                      minWidth: 'auto', 
                      p: 1,
                      color: 'rgba(255,255,255,0.7)',
                      '&:hover': {
                        color: '#f5f5dc'
                      }
                    }}
                  >
                    {showConfirmPassword ? <VisibilityOff sx={{ fontSize: 20 }} /> : <Visibility sx={{ fontSize: 20 }} />}
                  </Button>
                </InputAdornment>
              ),
            }}
            sx={textFieldStyles}
          />
          
          {formData.confirmPassword && (
            <Box display="flex" alignItems="center" gap={1} mt={1}>
              {formData.password === formData.confirmPassword ? (
                <>
                  <CheckCircleIcon sx={{ color: '#10b981', fontSize: 16 }} />
                  <Typography variant="caption" sx={{ color: '#10b981', fontSize: '0.75rem' }}>
                    {t('auth.passwordsMatch')}
                  </Typography>
                </>
              ) : (
                <Typography variant="caption" color="error.main" sx={{ fontSize: '0.75rem' }}>
                  {t('auth.passwordMismatch')}
                </Typography>
              )}
            </Box>
          )}
        </Stack>
      </Box>
    );
  }

  // Desktop Layout
  return (
    <Box sx={{ mb: { xs: 3, md: 4 } }}>
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t('auth.firstName')}
            name="firstName"
            value={formData.firstName}
            onChange={handleInputChange}
            required
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon sx={{ color: '#f5f5dc' }} />
                </InputAdornment>
              ),
            }}
            sx={textFieldStyles}
          />
        </Grid>
        
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label={t('auth.lastName')}
            name="lastName"
            value={formData.lastName}
            onChange={handleInputChange}
            required
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonIcon sx={{ color: '#f5f5dc' }} />
                </InputAdornment>
              ),
            }}
            sx={textFieldStyles}
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t('auth.email')}
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            required
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailIcon sx={{ color: '#f5f5dc' }} />
                </InputAdornment>
              ),
            }}
            sx={textFieldStyles}
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t('auth.phone')}
            name="phone"
            value={formData.phone}
            onChange={handleInputChange}
            placeholder={t('auth.phonePlaceholder')}
            required
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PhoneIcon sx={{ color: '#f5f5dc' }} />
                </InputAdornment>
              ),
            }}
            sx={textFieldStyles}
          />
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t('auth.password')}
            name="password"
            type={showPassword ? 'text' : 'password'}
            value={formData.password}
            onChange={handleInputChange}
            required
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ color: '#f5f5dc' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    onClick={() => setShowPassword(!showPassword)}
                    sx={{ 
                      minWidth: 'auto', 
                      p: 1,
                      color: 'rgba(255,255,255,0.7)',
                      '&:hover': {
                        color: '#f5f5dc'
                      }
                    }}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </Button>
                </InputAdornment>
              ),
            }}
            sx={textFieldStyles}
          />
          {formData.password && (
            <Box sx={{ mt: 1 }}>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  {t('auth.passwordStrength')}:
                </Typography>
                <Typography
                  variant="caption"
                  color={passwordStrength < 50 ? 'error.main' : passwordStrength < 75 ? 'warning.main' : 'success.main'}
                  sx={{ fontWeight: 600 }}
                >
                  {passwordStrength < 50 ? t('auth.weak') : passwordStrength < 75 ? t('auth.medium') : t('auth.strong')}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={passwordStrength}
                sx={{
                  height: 4,
                  borderRadius: 2,
                  backgroundColor: 'rgba(255,255,255,0.1)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: passwordStrength < 50 ? '#ef4444' : passwordStrength < 75 ? '#f59e0b' : '#10b981',
                    borderRadius: 2,
                  },
                }}
              />
            </Box>
          )}
        </Grid>
        
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t('auth.confirmPassword')}
            name="confirmPassword"
            type={showConfirmPassword ? 'text' : 'password'}
            value={formData.confirmPassword}
            onChange={handleInputChange}
            required
            variant="outlined"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockIcon sx={{ color: '#f5f5dc' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <Button
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    sx={{ 
                      minWidth: 'auto', 
                      p: 1,
                      color: 'rgba(255,255,255,0.7)',
                      '&:hover': {
                        color: '#f5f5dc'
                      }
                    }}
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </Button>
                </InputAdornment>
              ),
            }}
            sx={textFieldStyles}
          />
          {formData.confirmPassword && (
            <Box display="flex" alignItems="center" gap={1} mt={1}>
              {formData.password === formData.confirmPassword ? (
                <>
                  <CheckCircleIcon sx={{ color: '#10b981', fontSize: 16 }} />
                  <Typography variant="caption" sx={{ color: '#10b981' }}>
                    {t('auth.passwordsMatch')}
                  </Typography>
                </>
              ) : (
                <Typography variant="caption" color="error.main">
                  {t('auth.passwordMismatch')}
                </Typography>
              )}
            </Box>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default ClientSignUpForm;